/**
 * Attribution capture utility.
 * Captures UTM parameters, referrer, and ad platform click IDs.
 * Stores them in sessionStorage for use in tracking events.
 * Also tracks internal SPA navigation via a flag.
 */

const ATTRIBUTION_KEY = "hubzu_attribution";
const INTERNAL_NAV_KEY = "hubzu_internal_nav";

/**
 * Sanitize referrer to only include origin (protocol + hostname).
 * This prevents leaking sensitive query parameters or path information
 * from the referring page.
 * @returns {string|null} The sanitized referrer origin or null
 */
function getSanitizedReferrer() {
  const referrer = document.referrer;
  if (!referrer) return null;

  try {
    const url = new URL(referrer);
    // Only return origin (protocol + hostname + port)
    // This strips path, query params, and hash which could contain sensitive data
    return url.origin;
  } catch {
    // Invalid URL, don't store it
    return null;
  }
}

/**
 * Capture attribution data from the current URL and document.referrer.
 * - First call in a session: always captures
 * - Subsequent calls: only re-captures if current URL has UTM/click params
 *   AND stored data had no meaningful attribution (was a direct/internal visit)
 * - Once meaningful external attribution is stored, never overwrites
 */
export function captureAttribution() {
  const params = new URLSearchParams(window.location.search);

  const existing = sessionStorage.getItem(ATTRIBUTION_KEY);
  if (existing) {
    // Already captured — check if we should update
    const hasNewParams =
      params.get("utm_source") ||
      params.get("gclid") ||
      params.get("gclsrc") ||
      params.get("gad_source") ||
      params.get("fbclid") ||
      params.get("msclkid");

    if (!hasNewParams) return; // No new attribution in URL, keep existing

    // Check if stored data already has external attribution (don't overwrite)
    try {
      const stored = JSON.parse(existing);
      const hadExternalAttribution =
        stored.utm_source ||
        stored.gclid ||
        stored.gclsrc ||
        stored.gad_source ||
        stored.fbclid ||
        stored.msclkid;
      if (hadExternalAttribution) return; // Already have good external data
    } catch {
      // Corrupted data, re-capture
    }
  }

  const attribution = {
    utm_source: params.get("utm_source") || null,
    utm_medium: params.get("utm_medium") || null,
    utm_campaign: params.get("utm_campaign") || null,
    utm_content: params.get("utm_content") || null,
    utm_term: params.get("utm_term") || null,
    utm_id: params.get("utm_id") || null,
    sfmc_id: params.get("sfmc_id") || null,
    referrer: getSanitizedReferrer(),
    gclid: params.get("gclid") || null,
    gclsrc: params.get("gclsrc") || null,
    gad_source: params.get("gad_source") || null,
    gad_campaignid: params.get("gad_campaignid") || null,
    fbclid: params.get("fbclid") || null,
    msclkid: params.get("msclkid") || null,
  };

  sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
}

/**
 * Mark that the next page view is from internal navigation.
 * Uses localStorage so it works across tabs (window.open for PDP).
 */
export function markInternalNavigation() {
  // log
  localStorage.setItem(INTERNAL_NAV_KEY, "true");
}

/**
 * Check and consume the internal navigation flag.
 * Returns true if flag exists. Removes it immediately after reading.
 */
export function consumeInternalNavigation() {
  const isInternal = localStorage.getItem(INTERNAL_NAV_KEY) === "true";
  localStorage.removeItem(INTERNAL_NAV_KEY);
  return isInternal;
}

/**
 * Get the captured attribution data for the current session.
 * Returns an object with UTM params, referrer, and click IDs.
 */
export function getAttribution() {
  try {
    const stored = sessionStorage.getItem(ATTRIBUTION_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

/**
 * Determine the traffic source type based on attribution data.
 * Returns: 'paid', 'campaign', 'referral', 'direct', or 'internal'
 *
 * Priority:
 * 1. Internal SPA navigation flag (same-tab nav via navbar)
 * 2. Ad platform click IDs (paid)
 * 3. UTM params (paid or campaign)
 * 4. Referrer from same domain (internal — new tab opened from within app)
 * 5. Referrer from different domain (referral)
 * 6. No signal (direct)
 */
export function getSourceType() {
  // Check internal SPA navigation flag first (same-tab navigation)
  if (consumeInternalNavigation()) {
    return "internal";
  }

  const attribution = getAttribution();

  // Google Ads (gclid, gclsrc=aw.ds, gad_source)
  if (attribution.gclid || attribution.gclsrc || attribution.gad_source) {
    return "paid";
  }
  // Facebook Ads
  if (attribution.fbclid) {
    return "paid";
  }
  // Microsoft Ads
  if (attribution.msclkid) {
    return "paid";
  }
  // UTM-based campaigns
  if (attribution.utm_source) {
    return attribution.utm_medium === "cpc" || attribution.utm_medium === "paid"
      ? "paid"
      : "campaign";
  }
  // Referrer-based detection (works for new-tab PDP opened from within app)
  if (attribution.referrer) {
    try {
      // referrer is now stored as origin (e.g., "https://www.hubzu.com")
      const referrerUrl = new URL(attribution.referrer);
      const referrerHost = referrerUrl.hostname;
      const currentHost = window.location.hostname;
      // Same domain = internal (PDP opened in new tab from SRP/homepage)
      if (referrerHost === currentHost || referrerHost.endsWith(".hubzu.com")) {
        return "internal";
      }
    } catch {
      // Invalid referrer URL
    }
    return "referral";
  }
  return "direct";
}

/**
 * Build the utmInfo string for the filter-interaction API payload.
 * Extracts UTM ID, campaign name, UTM user ID (utm_content), and medium
 * from the captured attribution data.
 *
 * Format: "utmId=<utm_id>,campaignName=<utm_campaign>,utmUserId=<utm_content>,medium=<utm_medium>"
 * Returns null if no UTM attribution data is available.
 */
export function buildUtmInfo() {
  const attribution = getAttribution();

  const utmId = attribution.utm_id || null;
  const campaignName = attribution.utm_campaign || null;
  const utmUserId = attribution.utm_content || null;
  const medium = attribution.utm_medium || null;

  // Return null if none of the SFMC/UTM fields are present
  if (!utmId && !campaignName && !utmUserId && !medium) {
    return null;
  }

  const parts = [];
  parts.push(`utmId=${utmId || ""}`);
  parts.push(`campaignName=${campaignName || ""}`);
  parts.push(`utmUserId=${utmUserId || ""}`);
  parts.push(`medium=${medium || ""}`);

  return parts.join(",");
}
